import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'

import {updatePaymentBenefitInfo} from '@web/actions/benefitInfo'
import {selectCardPromotionGroup} from '@web/actions/cardPromotion'
import {initRewardBenefitByAPI} from '@web/actions/detail'
import {
    changeRechargePoint,
    SelectPaymentMethodActions,
    selectComplexPayMethod,
    setMaximumRechargePoint,
} from '@web/actions/paymentMethod'
import {
    changeAlwaysUsesAllNaverPoint,
    changeDeliveryAreaExtraFee,
    changeNaverPoint,
    changePayLaterPoint,
    changePointAndToggle,
    enablePayLaterPoint,
    enablePayLaterPointByAllPoint,
    resetNaverPoint,
    resetPayLaterPoint,
    updatePointPayment,
    usingAllNaverPoint,
    usingAllPayLaterPoint,
} from '@web/actions/pointPayment'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {fetchExpectedRewardPoint, getExpectedRewardPointV2} from '@web/actions/rewardPoint'
import {OrderSheetPageSet, PayMoneyUserStatus} from '@web/core/constants'
import {ChargePointState, getInitialState, convertExpectRewardInfo} from '@web/slices/chargePoint/slice'

import {ChargePointReducers, MakeSliceOptions} from './interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<ChargePointState, ChargePointReducers, 'chargePoint', SliceSelectors<ChargePointState>>({
        name: 'chargePoint',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(selectCardPromotionGroup.fulfilled, (state) => {
                    state.recharging.amount = 0
                })
                .addCase(initRewardBenefitByAPI.fulfilled, (state, {payload}) => {
                    state.payMoney.userStatus = payload.chargePointUserStatusType ?? PayMoneyUserStatus.NOT_OPEN
                })
                .addCase(updatePaymentBenefitInfo.fulfilled, (state, {payload}) => {
                    const {expectedAccumulateRateForRecharging} = payload

                    state.recharging.expectedAccumulateRate = expectedAccumulateRateForRecharging
                })
                .addCase(updatePaymentBenefitInfo.rejected, (state) => {
                    const initialState = getInitialState()
                    state.recharging.accumulateRate = {
                        ...initialState.recharging.accumulateRate,
                    }
                })
                .addCase(fetchExpectedRewardPoint.fulfilled, (state, {payload}) => {
                    if (payload.pageSet === OrderSheetPageSet.FAMILY_PAY) {
                        const {
                            paymoneyAcmRate = 0,
                            bankbookAcmRate = 0,
                            adminDefaultAcmRate = 0,
                            acmAmount,
                        } = payload.data

                        state.recharging.expectedAccumulateRate =
                            adminDefaultAcmRate + paymoneyAcmRate + bankbookAcmRate
                        state.recharging.expectedReward = acmAmount
                    } else {
                        const {
                            expectedChargePoint,
                            expectedNaverBankbook,
                            expectedAdminAcmAmount,
                            adminAcmRate,
                            paymoneyUseAcmRate,
                            bankbookAcmRate,
                        } = payload.data

                        state.recharging.expectedAccumulateRate = adminAcmRate + bankbookAcmRate + paymoneyUseAcmRate
                        state.recharging.expectedReward =
                            expectedAdminAcmAmount + expectedChargePoint + expectedNaverBankbook
                    }
                })
                .addCase(fetchExpectedRewardPoint.rejected, (state) => {
                    state.recharging.expectedAccumulateRate = 0
                    state.recharging.expectedReward = 0
                })
                .addCase(getExpectedRewardPointV2.fulfilled, (state, {payload: {items}}) => {
                    if (isEmpty(items)) {
                        state.recharging.expectedAccumulateRate = 0
                        state.recharging.expectedReward = 0

                        return state
                    }

                    const {expectedAccumulateRate, expectedReward} = convertExpectRewardInfo(items)

                    state.recharging.expectedAccumulateRate = expectedAccumulateRate
                    state.recharging.expectedReward = expectedReward

                    return state
                })
                .addCase(getExpectedRewardPointV2.rejected, (state, {payload}) => {
                    if (payload?.code === 'API_ERROR') {
                        state.recharging.expectedAccumulateRate = 0
                        state.recharging.expectedReward = 0
                    }
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(
                    isAnyOf(
                        changeRechargePoint.fulfilled,
                        setMaximumRechargePoint.fulfilled,
                        selectComplexPayMethod.fulfilled,
                        ...SelectPaymentMethodActions,
                    ),
                    (state, {payload}) => {
                        state.recharging.amount = payload.rechargingPointAmount
                    },
                )
                .addMatcher(
                    isAnyOf(
                        changeDeliveryAreaExtraFee.fulfilled,
                        changeNaverPoint.fulfilled,
                        resetNaverPoint.fulfilled,
                        usingAllNaverPoint.fulfilled,
                        changeAlwaysUsesAllNaverPoint.fulfilled,
                        changePayLaterPoint.fulfilled,
                        changePointAndToggle.fulfilled,
                        resetPayLaterPoint.fulfilled,
                        usingAllPayLaterPoint.fulfilled,
                        enablePayLaterPoint.fulfilled,
                        enablePayLaterPointByAllPoint.fulfilled,
                        updatePointPayment.fulfilled,
                    ),
                    (state, {payload}) => {
                        // 포인트 사용으로 남은 금액이 없다면 충전포인트 초기화
                        if (payload.paymentAmount <= 0) {
                            state.recharging.amount = 0
                        } else {
                            state.recharging.amount = payload.rechargingPointAmount
                        }
                    },
                )
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.chargePoint ?? state
                })
        },
    })
